import { CSSProperties, useState } from 'react';
import { ConnectDragSource, ConnectDropTarget, useDrag, useDrop } from 'react-dnd';

import { colors } from 'services/theme/constants';

const ItemType = 'ITEM';

export interface IDnDProps {
  item: { [key: string]: any };
  index: number | string;
  data: { [key: string]: any }[];
  callbackResultFn?: (sortedResults: { [key: string]: any }[]) => void;
  setIsHovered?: (booleanValue: boolean) => void;
}

type IDnD = Omit<IDnDProps, 'item'>;

type TDnD = {
  drag: ConnectDragSource;
  isDragging: boolean;
  isHovered: boolean;
  drop: ConnectDropTarget;
  defaultDnDStyle: CSSProperties;
};

// drag and drop hook
export const useDnD = ({ index, data, callbackResultFn }: IDnD): TDnD => {
  const [isHovered, setIsHovered] = useState(false);

  const moveItem = (from: number, to: number) => {
    const updated = [...data];
    const [moved] = updated.splice(from, 1);
    updated.splice(to, 0, moved);

    callbackResultFn && callbackResultFn(updated);
  };

  const [{ isDragging }, drag] = useDrag({
    type: ItemType,
    item: { index },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [__, drop] = useDrop({
    accept: ItemType,
    drop: (draggedItem: any) => {
      if (draggedItem.index !== index) {
        moveItem(draggedItem.index, Number(index));
        draggedItem.index = index;
      }
    },
    collect: monitor => {
      setIsHovered && setIsHovered(monitor.isOver());
    },
  });

  const defaultDnDStyle = {
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isHovered ? '#f0f0f0' : colors.white,
    transition: 'background-color 0.2s ease',
  };

  return { drag, isDragging, isHovered, drop, defaultDnDStyle };
};
